/**
 * Rendering Engine — the pipeline.
 *
 *   Design JSON V2  →  native iVisora editor document
 *
 * It receives a Design JSON V2 document and NOTHING else, and outputs native
 * editor JSON and nothing else. It creates all geometry: the model never
 * authors a pixel.
 *
 * Order of resolution:
 *   tokens → components → sections → auto layout → geometry → constraints
 *   → background → masks/effects/paint → editor mapping → groups → layers
 *
 * Pure, deterministic, and free of AI, research, thinking, memory, the library,
 * the art director and the revision loop. It only renders.
 */

import { SCHEMA_ID } from '../schema';
import {
  RENDERER_ID, RENDERER_VERSION, BAND_ORDER, SECTION_GAP, META_NAMESPACE, UNIT_SCALE,
} from './rendererSchema';
import { createTokenResolver } from './tokens';
import { expandComponents } from './components';
import { measure, place, layoutOf, applyConstraints, childrenOf, longestWordWidth } from './layout';
import { resolveFills, gradientToCss } from './paint';
import { mapNode, framesImage } from './elements';

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const asArray = (v) => (Array.isArray(v) ? v : []);
const round = (n) => Math.round(n * 100) / 100;

/** Stable ids so a rendered document is diffable and deterministic. */
const idFactory = (prefix) => {
  let n = 0;
  return (node) => {
    n += 1;
    return (node && node.id) || `${prefix}-${n}`;
  };
};

/** Give every node an id, in traversal order, before anything references them. */
const ensureIds = (nodes, nextId) => asArray(nodes).map((node) => {
  if (!isObj(node)) return node;
  const withId = { ...node, id: nextId(node) };
  if (Array.isArray(node.children)) return { ...withId, children: ensureIds(node.children, nextId) };
  // A frame's single `child` needs an id too.
  if (isObj(node.child)) return { ...withId, child: ensureIds([node.child], nextId)[0] };
  return withId;
});

/**
 * The page background system: the first paintable fill becomes the editor's
 * native page background; every remaining fill becomes a real background-band
 * element covering the canvas, which is what makes it a *system* and not a colour.
 */
const resolveBackground = (page, canvas, tokens) => {
  const fills = asArray(page.background).filter(isObj);
  if (fills.length === 0) return { background: tokens.color('surface', '#ffffff'), layers: [] };

  const [first, ...rest] = fills;
  let background;
  switch (first.kind) {
    case 'gradient': background = gradientToCss(first, tokens); break;
    case 'image': background = first.src || tokens.color('surface', '#ffffff'); break;
    case 'solid':
    default: background = tokens.color(first.color, '#ffffff'); break;
  }
  if (first.kind === 'pattern' || first.kind === 'noise' || first.kind === 'texture') {
    background = tokens.color(first.color, '#ffffff');
  }

  // Supporting fills become full-bleed nodes in the background band.
  const layers = rest.map((fill, i) => ({
    node: 'object', type: fill.kind === 'image' ? 'image' : 'shape',
    id: `${page.id || 'page'}-bg-${i + 1}`,
    role: 'background-layer', layer: 'background',
    shape: 'rect',
    x: 0, y: 0, width: canvas.width, height: canvas.height,
    fills: [fill],
    src: fill.src || null, query: fill.query || null, fit: fill.fit || 'cover',
    editable: { locked: true, content: false, style: true, position: false, size: false, swappable: fill.kind === 'image' },
  }));

  return { background, layers };
};

/** The canvas box content is laid out within, inset by the safe area. */
/**
 * The page margin never fully disappears, however tight the composition gets.
 * A flyer printed to the trim edge is a production fault, not a dense design.
 */
const MIN_SAFE_AREA_SCALE = 0.55;

/**
 * The area sections are laid out in.
 *
 * `spacingScale` tightens the safe-area margin along with every other gap and
 * pad. The margin is page padding — the same kind of air — so excluding it from
 * the negotiation left a design squeezed everywhere except its outer edge.
 */
const contentBox = (canvas, spacingScale = 1) => {
  const declared = isNum(canvas.safeArea) ? Math.min(canvas.width, canvas.height) * canvas.safeArea : 0;
  const inset = Math.round(declared * Math.max(MIN_SAFE_AREA_SCALE, spacingScale));
  return { x: inset, y: inset, width: Math.max(0, canvas.width - inset * 2), height: Math.max(0, canvas.height - inset * 2) };
};

/**
 * Sections stack down the content box. A section that declares a size keeps it;
 * the rest hug their content, and any leftover space is shared by the sections
 * that asked to fill.
 */
const placeSections = (sections, box, ctx) => {
  const gap = ctx.tokens.space(SECTION_GAP, 0);
  const measured = sections.map((section) => {
    const node = { node: 'frame', ...section, children: section.nodes };
    const l = layoutOf(node);
    const fills = l.sizing === 'fill';
    // Two different heights matter for a `fill` section. `measure` reports the
    // space OFFERED to it — for a filler that is the whole page — while what
    // the section needs to hold its own content is the height it measures with
    // the fill removed. Confusing the two made a hero claim the entire canvas.
    const natural = fills
      ? measure({ ...node, layout: { ...l, sizing: 'hug' } }, { width: box.width, height: box.height }, ctx)
      : null;
    return {
      section,
      node,
      fills,
      size: measure(node, { width: box.width, height: box.height }, ctx),
      naturalHeight: natural ? natural.height : null,
    };
  });

  const fixedTotal = measured.filter((m) => !m.fills).reduce((sum, m) => sum + m.size.height, 0);
  const gaps = gap * Math.max(0, measured.length - 1);
  const fillers = measured.filter((m) => m.fills).length;
  const spare = Math.max(0, box.height - fixedTotal - gaps);
  const perFiller = fillers ? spare / fillers : 0;

  let cursor = box.y;
  return measured.map(({ section, node, fills, size, naturalHeight }) => {
    // A `fill` section absorbs spare height — but never LESS than its own
    // content needs. Handing it the leftover regardless meant a hero section
    // whose copy and photograph needed 579px placed in 277px, so the features
    // section beneath was positioned inside it and the headline printed
    // straight through three benefit cards.
    const height = fills ? Math.max(perFiller, naturalHeight) : size.height;
    const placed = place(node, { x: box.x, y: cursor, width: box.width, height }, ctx);
    cursor += height + gap;
    return { section, placed };
  });
};

/** Depth-first flatten: parents are emitted with their children's parentId set. */
const flatten = (node, ctx, parentId, out) => {
  const element = mapNode(node, ctx);
  if (!element) return;
  element.parentId = parentId;
  // A frame that clips an image IS the image: its child is already absorbed.
  const children = framesImage(node) ? [] : childrenOf(node);

  if (children.length === 0) {
    out.push(element);
    return;
  }
  // Members first, then the container above them — the editor's grouping convention.
  const before = out.length;
  children.forEach((child) => flatten(child, ctx, element.id, out));
  if (out.length === before) return; // an empty container is not an element
  out.push(element);
};

/** Stable sort into layer bands: earlier bands sit behind later ones. */
const orderByBand = (elements) => {
  const bandOf = (el) => {
    const band = el[META_NAMESPACE] && el[META_NAMESPACE].layer;
    const i = BAND_ORDER.indexOf(band);
    return i === -1 ? BAND_ORDER.indexOf('content') : i;
  };
  return elements
    .map((el, i) => ({ el, i }))
    .sort((a, b) => (bandOf(a.el) - bandOf(b.el)) || (a.i - b.i))
    .map(({ el }) => el);
};

/** Render one page. */
/**
 * The height these sections need at this spacing, without placing anything.
 *
 * A `fill` section is measured with the fill removed, because what a filler
 * needs and what it is offered are different numbers.
 */
const sectionsHeight = (sections, box, ctx) => {
  const gap = ctx.tokens.space(SECTION_GAP, 0);
  return sections.reduce((total, section) => {
    const node = { node: 'frame', ...section, children: section.nodes };
    const l = layoutOf(node);
    const measurable = l.sizing === 'fill' ? { ...node, layout: { ...l, sizing: 'hug' } } : node;
    return total + measure(measurable, { width: box.width, height: box.height }, ctx).height;
  }, gap * Math.max(0, sections.length - 1));
};

/**
 * How much of the spacing scale a page needs in order to fit.
 *
 * A rich composition — the kind a strong model produces, with a header, an
 * offer, a hero, benefits, proof, a CTA and a footer — routinely wants more
 * height than the canvas has. The first thing to give up is the air between
 * things, never the content: no section is dropped, no type is shrunk, no
 * photograph is reduced. Gaps and padding tighten proportionally until the
 * design fits, and stop at a floor so a page cannot compress into a solid block.
 *
 * Descending steps rather than a solve because measurement is not linear in
 * spacing: narrower padding widens a column, which re-wraps its text and
 * changes the height again. Trying real values and asking "does it fit yet" is
 * honest about that; a closed-form factor would not be.
 */
const SPACING_LADDER = Object.freeze([1, 0.85, 0.7, 0.55, 0.45, 0.35, 0.28, 0.22]);

/**
 * How much of a row a `fill` child may claim, largest first.
 *
 * A hero photograph and a headline are in direct competition for the same row:
 * widen the picture and the copy column narrows, so the headline wraps onto
 * more lines and the section grows taller. Both cannot be maximised, so the
 * page tries the most generous hero first and gives ground only as far as
 * fitting the canvas actually requires.
 */
const FILL_FLOOR_LADDER = Object.freeze([0.5, 0.42, 0.35, 0.28, 0.22]);

/**
 * The spacing and fill settings this page will be laid out with.
 *
 * Two levers, tried in priority order: keep the focal image as large as
 * possible, and only then keep the air as generous as possible. Nothing here
 * removes content, changes a type size, or drops a section — the design that
 * comes out is the design that went in, resolved into the space available.
 */
/**
 * Uniform reductions of the whole composition, tried last.
 *
 * Some designs are simply larger than the page even with the air squeezed out
 * of them. Reducing everything by the same factor is the one remaining move
 * that costs the design nothing: every size, every gap and every relationship
 * keeps its proportion, so the hierarchy that made the piece good survives
 * exactly — it is the same design, set slightly smaller. That is very
 * different from shrinking the headline, which would change the design.
 *
 * It is last because a page that can fit at full size should.
 */
const CONTENT_SCALE_LADDER = Object.freeze([
  1, 0.96, 0.92, 0.88, 0.84, 0.8, 0.72, 0.64, 0.56, 0.48, 0.42, 0.36,
]);

/**
 * The settings this page will be laid out with.
 *
 * Three levers in priority order: set the design at full size if it fits, then
 * keep the focal image as large as possible, then keep the air as generous as
 * possible, and only when none of that is enough reduce the whole composition
 * uniformly. Nothing here removes content, drops a section, or changes one
 * size relative to another.
 */
const fitAtScale = (sections, canvas, doc, scale) => {
  let last = null;
  for (const fillFloor of FILL_FLOOR_LADDER) {
    for (const spacingScale of SPACING_LADDER) {
      const tokens = createTokenResolver(doc.tokens, { scale, spacingScale });
      const box = contentBox(canvas, spacingScale);
      const candidate = { tokens, spacingScale, box, fillFloor, scale };
      if (sectionsHeight(sections, box, { tokens, scale, fillFloor }) <= box.height) {
        return { ...candidate, fits: true };
      }
      last = candidate;
    }
  }
  return { ...last, fits: false };
};

/**
 * Any line of copy whose longest word is wider than the box it was given.
 *
 * Height alone cannot answer "does this fit". A headline set larger than its
 * column produces an element of exactly the right height whose words are
 * sliced down the middle, and every bounds check passes.
 */
const clippedText = (nodes, ctx) => {
  const found = [];
  const walk = (node) => {
    if (!isObj(node)) return;
    if (node.type === 'text' && isNum(node.width) && node.width > 0) {
      const style = ctx.tokens.typeStyle(node.style || 'body');
      if (longestWordWidth(node.text, style) > node.width + 1) found.push(node.id);
    }
    asArray(node.children).forEach(walk);
    if (isObj(node.child)) walk(node.child);
  };
  asArray(nodes).forEach(walk);
  return found;
};

const renderPage = (page, index, doc, tokens) => {
  const canvas = isObj(page.canvas) ? page.canvas : {};
  const width = isNum(canvas.width) ? canvas.width : 1080;
  const height = isNum(canvas.height) ? canvas.height : 1080;
  const resolvedCanvas = { ...canvas, width, height };
  const pageId = page.id || `page-${index + 1}`;
  // Design JSON V2 canvases are px or mm. Tokens, the type scale and intrinsic
  // sizes are authored in px, so a mm canvas resolves them into mm — otherwise
  // a 24px pad would consume 24mm and the geometry collapses.
  const scale = UNIT_SCALE[canvas.unit] || UNIT_SCALE.px;

  const nextId = idFactory(`${pageId}-el`);
  const sections = asArray(page.sections).filter(isObj).map((section, i) => ({
    ...section,
    id: section.id || `${pageId}-section-${i + 1}`,
    // Component instances are expanded before anything is measured.
    nodes: ensureIds(expandComponents(asArray(section.nodes), doc.components), nextId),
  }));

  // Spacing is negotiated before anything is placed, so the whole page — every
  // section, every nested gap and pad — is laid out at one consistent scale.
  // The page is fitted at successively smaller uniform scales until it both
  // fits the canvas AND sets its type in columns wide enough to hold it. The
  // first is a height question and cheap to measure; the second only becomes
  // visible once the design is placed, because a headline set too large for its
  // column produces a correctly-sized box full of sliced words.
  let ctx = null;
  let placedSections = null;
  let box = null;
  let fitted = null;

  for (const contentScale of CONTENT_SCALE_LADDER) {
    const candidate = fitAtScale(sections, resolvedCanvas, doc, scale * contentScale);
    const candidateCtx = {
      tokens: candidate.tokens,
      canvas: resolvedCanvas,
      scale: candidate.scale,
      fillFloor: candidate.fillFloor,
    };
    const placed = placeSections(sections, candidate.box, candidateCtx);

    ctx = candidateCtx;
    box = candidate.box;
    placedSections = placed;
    fitted = { ...candidate, contentScale };

    if (candidate.fits && !clippedText(placed.map((entry) => entry.placed), candidateCtx).length) break;
  }

  const pageTokens = ctx.tokens;
  const { background, layers } = resolveBackground({ ...page, id: pageId }, resolvedCanvas, pageTokens);

  const elements = [];
  // The background system paints first.
  ensureIds(layers, nextId).forEach((layer) => flatten(place(layer, { x: layer.x, y: layer.y, width: layer.width, height: layer.height }, ctx), ctx, null, elements));

  placedSections.forEach(({ section, placed }) => {
    const sectionCtx = { ...ctx, sectionId: section.id, sectionRole: section.role || 'content' };
    // A section is structure, not an element: its children are emitted directly.
    asArray(placed.children).forEach((child) => flatten(child, sectionCtx, null, elements));
  });

  return {
    id: pageId,
    name: page.name || `Page ${index + 1}`,
    background,
    elements: orderByBand(elements),
    [META_NAMESPACE]: {
      canvas: {
        width, height, unit: canvas.unit || 'px',
        orientation: canvas.orientation || null,
        bleed: isNum(canvas.bleed) ? canvas.bleed : 0,
        safeArea: isNum(canvas.safeArea) ? canvas.safeArea : 0,
        grid: isObj(canvas.grid) ? { ...canvas.grid } : null,
      },
      sections: placedSections.map(({ section, placed }) => ({
        id: section.id, role: section.role || 'content',
        bounds: { x: placed.x, y: placed.y, width: placed.width, height: placed.height },
      })),
      layers: asArray(page.layers).length ? [...page.layers] : [...BAND_ORDER],
    },
  };
};

/**
 * Render a Design JSON V2 document into a native iVisora editor document.
 *
 * @param {Object} document  Design JSON V2 — the only input.
 * @returns {Object} { canvas_width, canvas_height, metadata, pages: [{ id, name, background, elements }] }
 */
export const renderDesignDocument = (document) => {
  const doc = isObj(document) ? document : {};
  const tokens = createTokenResolver(doc.tokens);
  const pages = asArray(doc.pages).filter(isObj);
  const rendered = (pages.length ? pages : [{}]).map((page, i) => renderPage(page, i, doc, tokens));
  const first = rendered[0];

  return {
    canvas_width: first[META_NAMESPACE].canvas.width,
    canvas_height: first[META_NAMESPACE].canvas.height,
    metadata: {
      ...(isObj(doc.meta) ? doc.meta : {}),
      sourceSchema: doc.schema || SCHEMA_ID,
      sourceVersion: isNum(doc.version) ? doc.version : null,
      renderer: RENDERER_ID,
      rendererVersion: RENDERER_VERSION,
      tokens: tokens.table,
      variants: asArray(doc.variants).map((v) => (isObj(v) ? { ...v } : v)),
    },
    pages: rendered,
  };
};

/**
 * Re-anchor a rendered page for a different canvas size, using each element's
 * constraints. This is how a variant is produced without re-rendering.
 *
 * @param {Object} page   a rendered page.
 * @param {Object} target { width, height }
 * @returns {Object} the re-anchored page.
 */
export const renderVariant = (page, target) => {
  const from = page[META_NAMESPACE].canvas;
  const to = { width: isNum(target.width) ? target.width : from.width, height: isNum(target.height) ? target.height : from.height };

  return {
    ...page,
    elements: page.elements.map((el) => {
      const meta = el[META_NAMESPACE] || {};
      const box = applyConstraints({ x: el.x, y: el.y, width: el.width, height: el.height }, meta.constraints, from, to);
      return { ...el, ...box };
    }),
    [META_NAMESPACE]: { ...page[META_NAMESPACE], canvas: { ...from, ...to } },
  };
};

export const __internals = { resolveBackground, contentBox, placeSections, orderByBand, ensureIds, round, resolveFills };
